import { Button } from "@afas-doe-maar/ui/Button";
import { Container } from "@afas-doe-maar/ui/Container";
import { cn } from "@afas-doe-maar/ui/utils";
import { forwardRef, useEffect, useRef } from "react";
import type { VoteRouteProps } from "./VoteScreen";
import { voteOptionLabels } from "~/lib/constants";
import { useVotingSession } from "~/providers/votingSession/VotingSessionContext";

export const VoteVoting = forwardRef<HTMLDivElement, VoteRouteProps>(function VoteVoting(
  { transitionState },
  ref,
) {
  const flashingContainerRef = useRef<HTMLDivElement>(null);
  const { makeVote, voter } = useVotingSession();
  const vote = voter?.vote ?? null;

  useEffect(() => {
    if (!flashingContainerRef.current || !vote || transitionState !== "entered") return;

    const container = flashingContainerRef.current;
    let timeoutId: NodeJS.Timeout;
    let isFlashing = false;

    const flash = () => {
      if (isFlashing) {
        container.style.opacity = "0";
        isFlashing = false;
        timeoutId = setTimeout(flash, randomBetweeen(50, 300));
      } else {
        container.style.borderRadius = randomBorderRadius();
        container.style.opacity = "1";
        isFlashing = true;
        timeoutId = setTimeout(flash, randomBetweeen(50, 400));
      }
    };

    timeoutId = setTimeout(flash, randomBetweeen(500, 1500));

    return () => {
      clearTimeout(timeoutId);
      container.style.opacity = "0";
    };
  }, [transitionState, vote]);

  return (
    <Container
      ref={ref}
      className={cn(
        "pointer-events-none grid overflow-clip *:col-span-full *:row-span-full",
        {
          entering: "",
          entered: "pointer-events-auto",
          exiting: "",
          exited: "",
          unmounted: "",
        }[transitionState],
      )}
      style={{ "--b": "16px" } as any}
    >
      <div
        className={cn(
          "relative blur-xl",
          vote === "green" && "bg-green",
          vote === "pink" && "bg-pink",
        )}
        ref={flashingContainerRef}
      />

      <div className="grid overflow-clip rounded-md *:col-span-full *:row-span-full">
        <Button
          className={cn(
            "bg-green hover:bg-green/80 h-full w-full origin-top-right rotate-90 rounded-none transition-[clip-path,rotate,background] duration-500",
            {
              entering: "rotate-0",
              entered: "rotate-0",
              exiting: "rotate-90",
              exited: "rotate-90",
              unmounted: "",
            }[transitionState],
            vote && "bg-transparent hover:bg-transparent",
          )}
          style={{
            clipPath: {
              none: "polygon(0 0, 100% 0, 100% calc(33% - var(--b) / 2), 0 calc(66% - var(--b) * 2))",
              green: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
              pink: "polygon(0 0, 100% 0, 100% 0, 0 0)",
            }[vote ?? "none"],
          }}
          onClick={() => makeVote(vote ? null : "green")}
        >
          <span className="sr-only">Stem op groen</span>
        </Button>

        <Button
          className={cn(
            "bg-pink hover:bg-pink/80 h-full w-full origin-bottom-left rotate-90 rounded-none transition-[clip-path,rotate,background] duration-500",
            {
              entering: "rotate-0",
              entered: "rotate-0",
              exiting: "rotate-90",
              exited: "rotate-90",
              unmounted: "",
            }[transitionState],
            vote && "bg-transparent hover:bg-transparent",
          )}
          onClick={() => makeVote(vote ? null : "pink")}
          style={{
            clipPath: {
              none: "polygon(0 calc(66% - var(--b) / 2), 100% calc(33% + var(--b)), 100% 100%, 0 100%)",
              green: "polygon(0 100%, 100% 100%, 100% 100%, 0 100%)",
              pink: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
            }[vote ?? "none"],
          }}
        >
          <span className="sr-only">Stem op roze</span>
        </Button>
      </div>

      <div
        className={cn(
          "pointer-events-none z-10 flex translate-x-full flex-col items-center justify-center gap-4 opacity-0 transition-[translate,opacity] duration-500",
          {
            entering: "-translate-x-0 opacity-100",
            entered: "translate-x-0 opacity-100",
            exiting: "translate-x-full opacity-0",
            exited: "-translate-x-full opacity-0 duration-0",
            unmounted: "",
          }[transitionState],
        )}
      >
        <p
          className={
            "font-heading bg-background text-yellow px-4 py-1 text-center text-3xl font-bold text-balance"
          }
          style={{ clipPath: "polygon(16px 0, 100% 0, calc(100% - 16px) 100%, 0 100%)" }}
        >
          {vote ? `JE HEBT ${voteOptionLabels[vote].toUpperCase()} GESTEMD` : "STEM OP EEN KLEUR"}
        </p>
        {vote && (
          <p className="text-center font-bold text-balance">
            Hou je telefoon omhoog en laat iedereen zien wat je gestemd hebt
          </p>
        )}

        {vote && (
          <p className="text-muted-foreground absolute bottom-4 w-full text-center text-sm">
            Tik om je stem te wijzigen
          </p>
        )}
      </div>
    </Container>
  );
});

function randomBetweeen(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

function randomBorderRadius() {
  return (
    Array.from({ length: 4 }, () => randomBetweeen(30, 85) + "%").join(" ") +
    " / " +
    Array.from({ length: 4 }, () => randomBetweeen(30, 85) + "%").join(" ")
  );
}
